import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ArrowRight } from 'lucide-react'
import { article, jsonLdScript } from '@/lib/schema'

export const Route = createFileRoute('/_site/guides/slack-commands')({
  component: SlackCommandsGuidePage,
  head: () => ({
    meta: [
      { title: 'Slack Slash Commands for Uptime Monitoring — Uptrack' },
      { name: 'description', content: 'Check your uptime monitoring status directly from Slack. Use /uptrack status to see which sites are up or down without leaving your chat.' },
      { property: 'og:title', content: 'Slack Slash Commands — Uptrack' },
      { property: 'og:description', content: 'Type /uptrack status in Slack to check your monitors. No tab-switching needed.' },
      { property: 'og:image', content: 'https://uptrack.app/og/guides-slack-commands.png' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/guides/slack-commands' },
    ],
    scripts: [
      jsonLdScript(
        article({
          headline: 'Slack Slash Commands for Uptime Monitoring',
          description: 'Check your uptime monitoring status directly from Slack. Use /uptrack status to see which sites are up or down without leaving your chat.',
          url: 'https://uptrack.app/guides/slack-commands',
          datePublished: '2026-04-01',
          imageUrl: 'https://uptrack.app/og/guides-slack-commands.png',
        }),
      ),
    ],
  }),
})

function SlackCommandsGuidePage() {
  return (
    <>

      <>
        <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
          <h1 className="font-sans mb-4 text-4xl font-semibold">
            Check your uptime status from Slack
          </h1>
          <p className="mb-8 text-lg leading-[1.7] text-muted-foreground">
            Type <code className="rounded bg-muted px-1.5 py-0.5 text-sm font-mono">/uptrack status</code> in
            any Slack channel to see which sites are up, which are down, and what needs attention — without leaving your conversation.
          </p>

          {/* What you get */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">What it looks like</h2>
            <div className="space-y-4">
              <div className="rounded-lg border border-border bg-card p-5">
                <p className="mb-1 text-xs text-muted-foreground">When everything is healthy:</p>
                <div className="rounded bg-muted/50 p-4 font-mono text-sm">
                  <p className="font-semibold">Monitoring Status — 12 monitors</p>
                  <p className="text-status-up">All 12 monitors operational.</p>
                </div>
              </div>

              <div className="rounded-lg border border-status-down/20 bg-status-down/5 p-5">
                <p className="mb-1 text-xs text-muted-foreground">When something is down:</p>
                <div className="rounded bg-muted/50 p-4 font-mono text-sm">
                  <p className="font-semibold">Monitoring Status — 12 monitors</p>
                  <p>🟢 10 up  🔴 2 down</p>
                  <p className="mt-2 font-semibold">Down right now:</p>
                  <p>🔴 <span className="font-semibold">api.example.com</span></p>
                  <p>🔴 <span className="font-semibold">checkout.example.com</span></p>
                </div>
              </div>
            </div>
          </section>

          {/* Available commands */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Available commands</h2>
            <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Scrollable table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">Command</th>
                    <th className="pb-3 text-left font-medium text-muted-foreground">What it does</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b">
                    <td className="py-3 pr-4">
                      <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-sm">/uptrack status</code>
                    </td>
                    <td className="py-3 text-muted-foreground">
                      Shows total monitors, up/down/paused counts, and lists any down monitors by name. Visible to the whole channel.
                    </td>
                  </tr>
                  <tr className="border-b">
                    <td className="py-3 pr-4">
                      <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-sm">/uptrack list</code>
                    </td>
                    <td className="py-3 text-muted-foreground">
                      Lists up to 20 monitors with their status, name, URL, and check interval. Only visible to you.
                    </td>
                  </tr>
                  <tr>
                    <td className="py-3 pr-4">
                      <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-sm">/uptrack help</code>
                    </td>
                    <td className="py-3 text-muted-foreground">
                      Shows available commands.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* Setup */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Setup (2 minutes)</h2>
            <div className="space-y-6">
              <div className="flex gap-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-primary">1</div>
                <div>
                  <h3 className="font-sans mb-1 font-semibold">Connect Slack to Uptrack</h3>
                  <p className="text-lg leading-[1.7] text-body">
                    Go to <Link to="/dashboard/alerts" className="text-primary hover:underline">Dashboard → Alerts</Link> and
                    click "Connect to Slack". Authorize Uptrack in your workspace.
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-primary">2</div>
                <div>
                  <h3 className="font-sans mb-1 font-semibold">That's it</h3>
                  <p className="text-lg leading-[1.7] text-body">
                    The <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-xs">/uptrack</code> command
                    is automatically available in your workspace. Type it in any channel.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Why use it */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">Why check status from Slack?</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="rounded-lg border border-border bg-card p-5">
                <h3 className="font-sans mb-2 font-semibold">During incidents</h3>
                <p className="text-lg leading-[1.7] text-body">
                  When your team is in an incident channel, check which services are affected without switching to the dashboard.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <h3 className="font-sans mb-2 font-semibold">Morning stand-ups</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Quick status check in your team channel. "Everything operational" or "2 monitors need attention."
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <h3 className="font-sans mb-2 font-semibold">After deploys</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Just shipped? Run <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">/uptrack status</code> to
                  confirm your services came back up.
                </p>
              </div>
              <div className="rounded-lg border border-border bg-card p-5">
                <h3 className="font-sans mb-2 font-semibold">For non-technical teammates</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Customer support or sales can check service status without needing dashboard access.
                </p>
              </div>
            </div>
          </section>

          {/* FAQ */}
          <section className="mb-12">
            <h2 className="font-sans mb-4 text-2xl font-semibold">FAQ</h2>
            <div className="space-y-6">
              <div>
                <h3 className="font-sans mb-1 font-semibold">Who can see the response?</h3>
                <p className="text-lg leading-[1.7] text-body">
                  <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">/uptrack status</code> posts to the channel (everyone sees it).
                  <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">/uptrack list</code> is ephemeral (only you see it).
                </p>
              </div>
              <div>
                <h3 className="font-sans mb-1 font-semibold">Does it work in DMs?</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Yes. You can use the commands in any channel, group, or DM.
                </p>
              </div>
              <div>
                <h3 className="font-sans mb-1 font-semibold">What if I have 100+ monitors?</h3>
                <p className="text-lg leading-[1.7] text-body">
                  <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">/uptrack status</code> shows counts and lists up to 10 down monitors.
                  <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">/uptrack list</code> shows up to 20 monitors.
                  For the full list, use the dashboard.
                </p>
              </div>
              <div>
                <h3 className="font-sans mb-1 font-semibold">Which plans include it?</h3>
                <p className="text-lg leading-[1.7] text-body">
                  Slack alerts are available on every plan, one chat channel on the free plan. Slash commands need a paid plan.
                </p>
              </div>
            </div>
          </section>

          {/* CTA */}
          <div className="rounded-lg border border-border bg-muted p-8 text-center">
            <h2 className="font-sans mb-3 text-xl font-semibold">Start monitoring from Slack</h2>
            <p className="mb-6 text-lg leading-[1.7] text-muted-foreground">
              Pro includes 50 monitors at 30s, with Slack alerts and slash commands. The free plan has Slack alerts.
            </p>
            <Button size="lg" asChild>
              <Link to="/signup">
                See plans
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </article>
      </>

      </>
  )
}
